import Keycloak from 'keycloak-js';
import { USER_DATA_CLEARED_EVENT } from './userDataEvents';

import { clearPushSubscription } from '@/lib/pushNotifications';
import { stashPendingFocus } from '@/lib/pendingFocus';

// Environment variables for Keycloak configuration
const keycloakConfig = {
  url: import.meta.env.VITE_KEYCLOAK_URL || 'http://localhost:8080',
  realm: import.meta.env.VITE_KEYCLOAK_REALM || 'rosie',
  clientId: import.meta.env.VITE_KEYCLOAK_CLIENT_ID || 'andro-crew',
};

// Create Keycloak instance
export const keycloak = new Keycloak(keycloakConfig);

// Initialization options with PKCE (S256 method for secure token exchange)
export const keycloakInitOptions = {
  onLoad: 'login-required' as const,
  pkceMethod: 'S256' as const,
  checkLoginIframe: false,
};

// Routes that must render without authentication. Guests (friends voting on a
// meetup poll, anyone opening a shared chat) have no account, so Keycloak's
// onLoad: 'login-required' must NOT run on these paths or it redirects them to
// the login page during init before React can render the public page.
const PUBLIC_ROUTE_PREFIXES = ['/meetup/', '/s/', '/rsvp/'];

export function isPublicRoute(pathname: string = window.location.pathname): boolean {
  return PUBLIC_ROUTE_PREFIXES.some((prefix) => pathname.startsWith(prefix));
}

/**
 * Dedicated sign up entry. Marketing "Start free" / "Get Personal" buttons link
 * to /signup so a brand new visitor lands on the Keycloak REGISTRATION form
 * instead of the sign in form (where "Register" is only a small link).
 *
 * Not a "public route" in the sense above: it still needs Keycloak initialised
 * (login() is a no-op before init), but with a check-sso init instead of
 * login-required so a guest is not bounced to the sign in form first. See
 * initKeycloak() and register().
 */
const SIGNUP_PATH = '/signup';

export function isSignupRoute(pathname: string = window.location.pathname): boolean {
  return pathname === SIGNUP_PATH || pathname === `${SIGNUP_PATH}/`;
}

/**
 * Errors Keycloak returns in the URL fragment for a prompt=none (check-sso)
 * request when nobody has an SSO session. On /signup this is the normal
 * "guest" outcome, not a broken auth flow, so it must not trigger recovery.
 * (public/pkce-recovery.js carries the same exemption.)
 */
const CHECK_SSO_GUEST_ERRORS = ['login_required', 'interaction_required'];

/**
 * Keys holding Keycloak/PKCE state.
 *
 * keycloak-js stores the PKCE code_verifier and kc-callback-* entries in
 * localStorage. These are cleared both on logout and when recovering a broken
 * auth flow (a deployment can invalidate an in-flight code_verifier).
 */
function isAuthStorageKey(key: string): boolean {
  return key.startsWith('kc-') || key.includes('keycloak') || key.includes('oidc');
}

/**
 * Keys holding credentials or user content that do NOT carry a Keycloak prefix.
 *
 * These are cleared on logout only - never during PKCE recovery, which must not
 * destroy user data just because an auth redirect needs restarting.
 *
 *  - `meetup_response_<token>`  a bearer credential: the stored responseToken is
 *                               replayed to authorise later poll mutations.
 *  - `plaid_link_token`         Plaid Link credential; the component removes it
 *  - `plaid_link_institution`   on success, but not on an abandoned flow.
 *  - `chat-storage`             persists unsent chat drafts (user content) via
 *                               the store's partialize.
 *  - `rosie:return-url`         the path the user was last on, which can name a
 *                               chat id.
 *  - `andi:connector-notice`    a translation key parked mid OAuth round trip
 *                               for the connector connect that comes back. Per
 *                               flow ephemera naming neither user nor server,
 *                               so clearing it is about correctness rather than
 *                               secrecy: left behind it is collected by the
 *                               NEXT successful connect in this tab, which
 *                               after a logout is somebody else's.
 *
 * Deliberately NOT cleared, because they identify no user and losing them only
 * degrades the experience: `rosie-theme`, `rosie-archive-view`, `ui-storage`,
 * and `andi:chunk-reloaded-at`.
 */
function isSensitiveNonAuthKey(key: string): boolean {
  return (
    key.startsWith('meetup_response_') ||
    key === 'plaid_link_token' ||
    key === 'plaid_link_institution' ||
    key === 'chat-storage' ||
    key === 'rosie:return-url' ||
    key === 'andi:connector-notice'
  );
}

/**
 * Enumerate a Storage via the standard Storage.key(i) API.
 *
 * Used instead of Object.keys(storage) so that every enumeration in this module
 * (clearing auth keys, probing for PKCE data) goes through the same, spec-defined
 * path.
 */
function storageKeys(storage: Storage): string[] {
  const keys: string[] = [];
  for (let i = 0; i < storage.length; i++) {
    const key = storage.key(i);
    if (key !== null) {
      keys.push(key);
    }
  }
  return keys;
}

function removeMatching(storage: Storage, predicate: (key: string) => boolean): void {
  // Keys are collected before removal so that removing an entry cannot shift
  // the indices of entries not yet visited.
  storageKeys(storage)
    .filter(predicate)
    .forEach((key) => {
      storage.removeItem(key);
    });
}

/** Remove Keycloak/PKCE state only. Safe to call mid-auth-flow. */
function clearKeycloakData(): void {
  removeMatching(localStorage, isAuthStorageKey);
  removeMatching(sessionStorage, isAuthStorageKey);
}

/**
 * Delete every Cache Storage entry that can hold user data.
 *
 * The service worker no longer runtime-caches `/api/**` at all: the
 * NetworkFirst rule that wrote authenticated responses into `api-cache` is
 * gone from vite.config.ts, and push-sw.js deletes any cache an older SW left
 * behind when the replacement activates.
 *
 * This clear stays, for two reasons. A device whose service worker has not yet
 * updated is still running the old SW and still filling `api-cache` with this
 * user's chats, files, knowledge base content and billing data, so signing out
 * there must still remove it. And it is the standing guarantee that no future
 * runtime cache can outlive a session by being added without anyone
 * remembering this function exists.
 *
 * Workbox precaches are left alone: they hold only versioned static assets
 * (JS/CSS/HTML), no user data, and dropping them would force a full re-download
 * on the next load.
 *
 * The `workbox-expiration` IndexedDB database is deleted as well. Workbox's
 * expiration plugin keeps one row per cached entry holding the cache name, the
 * request URL and a timestamp, so it would otherwise retain a list of the
 * previous user's API URLs after the caches themselves are gone. caches.delete()
 * does not touch it. Workbox recreates the database on next use.
 */
async function clearCachedResponses(): Promise<void> {
  if (typeof caches !== 'undefined') {
    const names = await caches.keys();
    await Promise.all(
      names
        .filter((name) => !name.startsWith('workbox-precache'))
        .map((name) => caches.delete(name))
    );
  }
  if (typeof indexedDB !== 'undefined') {
    indexedDB.deleteDatabase('workbox-expiration');
  }
}

/**
 * Remove everything a signed-out user must not leave behind on the device:
 * Keycloak/PKCE state, non-prefixed credentials and user content, and the
 * cached API responses.
 *
 * Non-sensitive UI preferences (theme, language) are deliberately preserved.
 *
 * Exported because a session can end by expiry as well as by explicit logout;
 * useSessionExpiration calls this when silent refresh has failed, so both paths
 * leave the same (empty) residue behind.
 */
export async function clearAllUserData(): Promise<void> {
  // Listeners (the vault popup relay) stop work for the departing user.
  try {
    window.dispatchEvent(new Event(USER_DATA_CLEARED_EVENT));
  } catch {
    // No window (tests without a DOM): nothing is listening.
  }
  clearKeycloakData();
  removeMatching(localStorage, isSensitiveNonAuthKey);
  removeMatching(sessionStorage, isSensitiveNonAuthKey);
  await clearCachedResponses();
  // The Web Push subscription is per-device credential material registered to
  // the signed-in user: leaving it in place lets a logged-out device keep
  // receiving (and displaying) that user's notifications. Best-effort, and
  // deliberately last so a failure here cannot prevent the storage clear.
  await clearPushSubscription().catch(() => {});
}

/**
 * Set for the rest of the page's life as soon as logout() starts.
 *
 * logout() hands off to Keycloak only after the async Cache Storage clear
 * settles, so there is a window (up to CACHE_CLEAR_TIMEOUT_MS) in which the app
 * is still running with a valid Keycloak SSO cookie. In that window
 * authStore.logout() has already reset `isInitialized`, which re-runs useAuth's
 * init effect, and AuthGate calls login() whenever it sees
 * `isInitialized && !isAuthenticated`. Either one navigates to Keycloak's
 * *authorization* endpoint, which pre-empts the pending logout and - because
 * the SSO cookie is still valid - silently signs the user back in, so the sign
 * out never happens. Both entry points are closed off while this is set; the
 * pending keycloak.logout() is the only navigation allowed to win.
 */
let loggingOut = false;

/**
 * True while a logout redirect is pending in THIS tab.
 *
 * Read by the cross-tab session listener: a tab that has already started
 * signing itself out must not start a second redirect when its own broadcast
 * (or another tab's) comes back around.
 */
export function isLoggingOut(): boolean {
  return loggingOut;
}

/**
 * BroadcastChannel name for cross-tab session events.
 *
 * Owned here rather than in the listening hook because logout() is the single
 * funnel every explicit sign out passes through, so the broadcast belongs
 * next to it. The import direction is deliberate and one way: the hook imports
 * this module, never the reverse, so the two cannot form a cycle.
 */
export const SESSION_CHANNEL_NAME = 'rosie:session-sync';

/** Tab identity for cross-tab session events, stable for the page's life. */
export const sessionTabId = `tab-${String(Date.now())}-${Math.random().toString(36).substring(2, 11)}`;

/**
 * Tell every other tab on this origin that the user signed out.
 *
 * postMessage captures its destination list synchronously per the HTML spec,
 * so closing the sender immediately does not cancel delivery. The redirect is
 * in any case deferred behind clearAllUserData() (up to CACHE_CLEAR_TIMEOUT_MS),
 * so there is ample time for delivery either way.
 */
function broadcastSessionLogout(): void {
  if (typeof BroadcastChannel === 'undefined') return;
  try {
    const channel = new BroadcastChannel(SESSION_CHANNEL_NAME);
    channel.postMessage({ type: 'logout', timestamp: Date.now(), tabId: sessionTabId });
    channel.close();
  } catch (error) {
    console.error('Failed to broadcast cross-tab logout:', error);
  }
}

/**
 * Redirect to a clean URL (origin + pathname only, no hash/query params).
 * This restarts the auth flow from scratch.
 */
function redirectToCleanUrl(): void {
  window.location.href = window.location.origin + window.location.pathname;
}

/**
 * Initialize Keycloak authentication
 * @returns Promise resolving to true if authenticated, false otherwise
 */
export async function initKeycloak(): Promise<boolean> {
  // A logout redirect is already pending - see `loggingOut`. Re-initializing
  // here would also throw ("a 'Keycloak' instance can only be initialized
  // once") and log a spurious error.
  if (loggingOut) {
    return false;
  }

  // Never run the login-required init on public routes; a guest must be able to
  // open the page without being bounced to Keycloak. The page uses its own
  // unauthenticated API client, so no token is needed here.
  if (isPublicRoute()) {
    return false;
  }

  // Before any redirect below: a main chat `?focus=` link must survive the
  // recovery path (redirectToCleanUrl drops the query).
  stashPendingFocus();

  const hashParams = new URLSearchParams(window.location.hash.slice(1));
  const error = hashParams.get('error');
  const code = hashParams.get('code');

  const signupRoute = isSignupRoute();
  const isGuestCheckSsoResult =
    signupRoute && error !== null && CHECK_SSO_GUEST_ERRORS.includes(error);

  // Check for auth error in URL fragment (Keycloak returns errors in hash)
  if (error && !isGuestCheckSsoResult) {
    console.warn('Auth error detected in URL:', error, hashParams.get('error_description'));
    clearKeycloakData();
    redirectToCleanUrl();
    return false;
  }

  // Check for PKCE state mismatch BEFORE init:
  // If we have a code (callback from Keycloak) but no PKCE data in localStorage,
  // keycloak-js will fail with "Security error". Detect and recover proactively.
  // Note: keycloak-js stores PKCE data (kc-callback-*, code_verifier) in localStorage, not sessionStorage.
  if (code) {
    const hasPkceData = storageKeys(localStorage).some(
      (key) => key.startsWith('kc-callback-') || key.includes('code_verifier')
    );

    if (!hasPkceData) {
      console.warn(
        'Auth callback detected but no PKCE data found in localStorage - clearing and restarting auth flow'
      );
      clearKeycloakData();
      redirectToCleanUrl();
      return false;
    }
  }

  try {
    // /signup: check-sso (a prompt=none round trip) instead of login-required.
    // A guest comes back unauthenticated and is sent to the registration form
    // by register(); someone who already has an SSO session comes back
    // authenticated and is sent straight into the app. This matters because
    // Keycloak's registrations endpoint expires the identity cookie, so
    // registering while signed in would silently sign them out.
    const authenticated = await keycloak.init(
      signupRoute ? { ...keycloakInitOptions, onLoad: 'check-sso' as const } : keycloakInitOptions
    );
    return authenticated;
  } catch (error) {
    console.error('Keycloak initialization failed:', error);

    // If init fails and we have a code in URL, it's likely a PKCE mismatch
    if (code) {
      console.warn(
        'Init failed with auth code in URL - likely PKCE mismatch, restarting auth flow'
      );
      clearKeycloakData();
      redirectToCleanUrl();
      return false;
    }

    return false;
  }
}

/**
 * Redirect to Keycloak login page.
 * Only works after successful keycloak.init() - the adapter is set up during init.
 */
export function login(): void {
  // A logout redirect is already pending - see `loggingOut`. Navigating to the
  // authorization endpoint now would cancel it and silently re-authenticate
  // against the still-valid SSO cookie.
  if (loggingOut) {
    return;
  }

  // Guard against calling login before init completes. keycloak-js sets up its
  // adapter during init() and login() delegates to it, so calling early throws.
  // The adapter itself is a private field, so `didInitialize` is the only
  // supported way to detect this.
  if (!keycloak.didInitialize) {
    console.error('Keycloak not initialized. Call initKeycloak() first.');
    return;
  }
  void keycloak.login();
}

/**
 * Redirect to the Keycloak REGISTRATION form (not the sign in form).
 *
 * keycloak-js: `login({ action: 'register' })` sends the user to the realm's
 * /protocol/openid-connect/registrations endpoint, "If the value is `register`
 * then user is redirected to registration page, otherwise to login page"
 * (https://www.keycloak.org/securing-apps/javascript-adapter). After
 * registering, Keycloak redirects to `redirectUri`, which is the app root, so
 * the normal login-required init completes the sign in there.
 *
 * Same guards as login(): honours the logout-in-progress flag and requires
 * init() to have run.
 */
export function register(): void {
  if (loggingOut) {
    return;
  }
  if (!keycloak.didInitialize) {
    console.error('Keycloak not initialized. Call initKeycloak() first.');
    return;
  }
  void keycloak.login({ action: 'register', redirectUri: `${window.location.origin}/` });
}

/**
 * Logout and redirect to origin.
 *
 * Clears Keycloak/OIDC/PKCE artifacts from local and session storage before
 * handing off to Keycloak RP-initiated logout, so no authentication material
 * survives the session on a shared device. Tokens themselves are held in
 * memory by keycloak-js and are never persisted (authStore partialize is {}).
 */
export function logout(): void {
  // Re-entrancy guard. Without it a second logout() (a double-clicked menu
  // item, or the cross-tab listener reacting to a broadcast this tab itself
  // sent) re-runs the whole clear-and-redirect sequence and re-broadcasts,
  // which is what would let two tabs ping-pong logout messages at each other.
  if (loggingOut) return;
  // Storage clearing is synchronous and happens first, so it is already done
  // even if the steps below are cut short. Cache Storage is async, so the
  // redirect is deferred until it settles - otherwise navigation cancels the
  // deletion and the cached API responses survive the logout. The timeout
  // guarantees a stalled Cache API can never trap the user in a signed-in
  // session; the redirect wins either way.
  const CACHE_CLEAR_TIMEOUT_MS = 2000;
  // Claim the navigation before yielding: nothing else may redirect until the
  // Keycloak logout below has fired. Set BEFORE broadcasting so a receiving
  // tab's echo can never re-enter this function.
  loggingOut = true;
  // Every explicit sign out funnels through here, so putting the broadcast
  // here (rather than on the sign-out button) means every present and future
  // logout path syncs other tabs automatically. Without it, signing out in one
  // tab left every other tab fully authenticated (A.08).
  broadcastSessionLogout();
  const cleared = clearAllUserData().catch((error: unknown) => {
    console.error('Failed to fully clear local data on logout:', error);
  });
  const timedOut = new Promise<void>((resolve) => setTimeout(resolve, CACHE_CLEAR_TIMEOUT_MS));

  void Promise.race([cleared, timedOut]).finally(() => {
    void keycloak.logout({ redirectUri: window.location.origin });
  });
}

/**
 * Redirect to the Keycloak-hosted Account Console (self-service account
 * management: profile, password change, sign-in devices, and active sessions).
 * Built manually (not via keycloak.accountManagement()/createAccountUrl(),
 * which unconditionally append referrer/referrer_uri) so the console doesn't
 * show a "Back to <client>" link. The app never handles passwords itself;
 * credential management lives entirely in Keycloak.
 */
export function accountManagement(): void {
  // See login(): the adapter is private, so didInitialize is the supported
  // way to check that init() has run.
  if (!keycloak.didInitialize) {
    console.error('Keycloak not initialized. Call initKeycloak() first.');
    return;
  }
  // Mirrors the guard inside keycloak-js's createAccountUrl(): no account
  // console when configured against a generic OIDC provider without a realm.
  if (!keycloak.authServerUrl || !keycloak.realm) {
    console.error('Failed to open the Keycloak account console: adapter has no realm URL.');
    return;
  }
  const realmUrl = `${keycloak.authServerUrl.replace(/\/$/, '')}/realms/${encodeURIComponent(keycloak.realm)}`;
  window.location.assign(`${realmUrl}/account/`);
}

/**
 * Get the current access token
 * @returns The access token if authenticated, undefined otherwise
 */
export function getToken(): string | undefined {
  return keycloak.token;
}

/** Upper bound on one keycloak.updateToken call. */
const TOKEN_REFRESH_TIMEOUT_MS = 15_000;

/**
 * Refresh the access token if it will expire within the specified time
 * @param minValidity - Minimum validity time in seconds (default: 30)
 * @returns Promise resolving to true if token was refreshed, false otherwise
 */
export async function refreshToken(minValidity = 30): Promise<boolean> {
  try {
    // keycloak.updateToken has no timeout of its own: a hung token endpoint
    // would stall every caller (stream start, voice connect) indefinitely.
    // A rejection still propagates to the catch below.
    let timer: ReturnType<typeof setTimeout> | undefined;
    const timeout = new Promise<'timeout'>((resolve) => {
      timer = setTimeout(() => {
        resolve('timeout');
      }, TOKEN_REFRESH_TIMEOUT_MS);
    });
    let refreshed: boolean | 'timeout';
    try {
      refreshed = await Promise.race([keycloak.updateToken(minValidity), timeout]);
    } finally {
      clearTimeout(timer);
    }
    if (refreshed === 'timeout') {
      console.error('Token refresh timed out');
      return false;
    }
    return refreshed;
  } catch (error) {
    console.error('Token refresh failed:', error);
    return false;
  }
}

/**
 * Check if the user is currently authenticated
 * @returns true if authenticated, false otherwise
 */
export function isAuthenticated(): boolean {
  // Keycloak can return undefined when not initialized
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  return keycloak.authenticated ?? false;
}

/**
 * User information extracted from the token
 */
export interface KeycloakUserInfo {
  id: string | undefined;
  username: string | undefined;
  email: string | undefined;
  name: string | undefined;
  roles: string[];
}

/**
 * Get user information from the parsed token
 * @returns User info object if authenticated, null otherwise
 */
export function getUserInfo(): KeycloakUserInfo | null {
  if (!keycloak.tokenParsed) return null;
  const tokenParsed = keycloak.tokenParsed as {
    sub?: string;
    preferred_username?: string;
    email?: string;
    name?: string;
    realm_access?: { roles?: string[] };
  };
  return {
    id: tokenParsed.sub,
    username: tokenParsed.preferred_username,
    email: tokenParsed.email,
    name: tokenParsed.name,
    roles: tokenParsed.realm_access?.roles ?? [],
  };
}
